<template>
  <ConductSkeletonLoader v-if="isLoading" />
  <div v-else>
    <v-row dense>
      <v-col>
        <v-combobox
          label="Ação"
          v-model="actionFacilitator"
          dense
          hide-details
          outlined
          :items="actionFacilitators"
          item-text="value"
          @change="handleActionFacilitatorChange"
          background-color="white"
          :disabled="attendanceEsigned"
        />
      </v-col>

      <v-col>
        <v-combobox
          v-model="facilitator"
          label="Detalhes"
          dense
          outlined
          hide-details
          :items="detailFacilitators"
          item-text="value"
          @change="handleDetailFacilitatorChange"
          background-color="white"
          :disabled="attendanceEsigned"
        >
          <template v-slot:item="{ item }">
            <v-tooltip top max-width="450">
              <template v-slot:activator="{ on, attrs }">
                <span :attrs="attrs" v-on="on">
                  {{ item.value | truncate(60) }}
                </span>
              </template>

              <span>
                {{ item.value }}
              </span>
            </v-tooltip>
          </template>
        </v-combobox>
      </v-col>
    </v-row>

    <v-row dense>
      <v-col>
        <v-combobox
          v-model="facilitator"
          label="Periodicidade"
          dense
          hide-details
          outlined
          :items="intervalFacilitators"
          item-text="value"
          @change="handleFacilitatorChange"
          background-color="white"
          :disabled="attendanceEsigned"
        />
      </v-col>

      <v-col>
        <v-combobox
          v-model="facilitator"
          label="Periodo"
          dense
          hide-details
          outlined
          :items="periodFacilitators"
          item-text="value"
          @change="handleFacilitatorChange"
          background-color="white"
          :disabled="attendanceEsigned"
        />
      </v-col>

      <v-col>
        <v-combobox
          v-model="facilitator"
          label="Local"
          dense
          hide-details
          outlined
          :items="siteFacilitators"
          item-text="value"
          @change="handleFacilitatorChange"
          background-color="white"
          :disabled="attendanceEsigned"
        />
      </v-col>
    </v-row>

    <v-row dense>
      <v-col>
        <v-textarea
          v-model="conduct.observation"
          v-uppercase
          outlined
          label="Conduta"
          background-color="white"
          auto-grow
          :hide-details="!hasErrors"
          :error-messages="errors.observation"
          :disabled="attendanceEsigned"
          @input="handleObservationChange"
        />
      </v-col>
    </v-row>

    <v-spacer />
    <v-row align="center">
      <v-col cols="auto">
        <v-btn
          color="light-blue"
          dark
          @click="saveManager.triggerManualSave()"
          :loading="saveManager.isSaving"
        >
          Salvar Conduta
        </v-btn>
      </v-col>
      <v-col cols="auto">
        <SaveStatus
          :saving="saveManager.isSaving"
          :pending="saveManager.pendingSave"
          :saved-at="conduct.updatedAt"
          :loading="conductLoading"
          :error="hasErrors || saveManager.isError"
          not-saved-message="A conduta ainda não foi salva."
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import ConductSkeletonLoader from "@/components/medical_record/conduct/skeleton_loader.vue";
import SaveStatus from "@/components/ui/form/save_status/index.vue";

import { AutoSaveManager } from "@/services/AutoSaveManager/index.js";
import { normalizeString } from "@/helpers/string_helpers.js";
import { applyFacilitator } from "@/components/medical_record/conduct/apply_facilitator.js";

import GET_CONDUCT_FACILITATORS from "@/graphql/conduct/conduct_facilitators.gql";
import GET_CONDUCT from "@/graphql/conduct/conduct.gql";
import SAVE_CONDUCT from "@/graphql/conduct/save_conduct.gql";

export default {
  name: "MedicalRecordConductForm",
  components: {
    SaveStatus,
    ConductSkeletonLoader,
  },
  props: {
    attendance: Object,
    attendanceEsigned: Boolean,
  },
  data() {
    return {
      conduct: { observation: "" },
      conductFacilitators: [],
      actionFacilitator: [],
      facilitator: [],
      saveManager: null,
      errors: {},
    };
  },
  apollo: {
    conductFacilitators: {
      query: GET_CONDUCT_FACILITATORS,
    },
    conduct: {
      query: GET_CONDUCT,
      fetchPolicy: "no-cache",
      variables() {
        return {
          attendanceId: this.attendance.id,
        };
      },
    },
  },
  created() {
    this.saveManager = new AutoSaveManager({
      formName: "Conduta",
      beforeSave: () => this.beforeSave(),
      onSave: () => this.onSave(),
    });
  },
  watch: {
    $route() {
      this.triggerManualSave();
    },
  },
  computed: {
    isLoading() {
      return (
        this.conductLoading || this.$apollo.queries.conductFacilitators.loading
      );
    },
    conductLoading() {
      return !this.conduct.id;
    },
    actionFacilitators() {
      return this.lodash.filter(this.orderedFacilitators, {
        category: "action",
      });
    },
    detailFacilitators() {
      return this.lodash.filter(this.orderedFacilitators, {
        category: "detail",
        parent: { id: this.actionFacilitator.id },
      });
    },
    intervalFacilitators() {
      return this.lodash.filter(this.orderedFacilitators, {
        category: "interval",
      });
    },
    periodFacilitators() {
      return this.lodash.filter(this.orderedFacilitators, {
        category: "period",
      });
    },
    siteFacilitators() {
      return this.lodash.filter(this.orderedFacilitators, { category: "site" });
    },
    orderedFacilitators() {
      return this.lodash.orderBy(
        this.conductFacilitators,
        [(e) => normalizeString(e.value)],
        ["asc"]
      );
    },
    hasErrors() {
      return Object.keys(this.errors).length > 0;
    },
  },
  methods: {
    handleActionFacilitatorChange(facilitator) {
      if (this.attendanceEsigned) return;

      this.handleFacilitatorChange(facilitator, { breakLine: true });
    },
    handleDetailFacilitatorChange(facilitator) {
      if (this.attendanceEsigned) return;

      this.handleFacilitatorChange(facilitator);
      this.actionFacilitator = [];
    },
    handleFacilitatorChange(facilitator, { breakLine = false } = {}) {
      if (this.attendanceEsigned) return;

      this.$set(
        this.conduct,
        "observation",
        applyFacilitator({
          value: facilitator?.value,
          observation: this.conduct.observation,
          breakLine: breakLine,
        })
      );

      this.facilitator = [];

      this.saveManager.dispatchAutoSave();
    },
    handleObservationChange() {
      this.saveManager.dispatchAutoSave();
    },
    validateConduct() {
      this.errors = {};

      if (!this.conduct.observation?.trim()) {
        this.errors.observation = "A conduta não pode estar vazia.";
      }
    },
    beforeSave() {
      if (this.attendanceEsigned) return false;

      this.validateConduct();
      if (this.hasErrors) return false;

      return true;
    },
    async onSave() {
      const { data } = await this.apolloMutate({
        mutation: SAVE_CONDUCT,
        variables: {
          attendanceId: this.attendance.id,
          observation: this.conduct.observation,
        },
      });

      this.$set(this.conduct, "updatedAt", data.saveConduct.updatedAt);
    },
  },
};
</script>

<style scoped></style>
